import { Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { filter } from 'rxjs/operators'
import { CrudOps } from '../../../api/constants/crud.enum'
import { Employee, EmployeePaginatedList } from '../../../api/models/employee.model'
import { TimeCardSearchCriteria, TimeCardStatus } from '../../../api/models/timecard.model'
import { TimesheetProjectPaginatedList } from '../../../api/models/timesheet-project.model'
import { EmployeeService } from '../../../api/services/employee.service'
import { TimeCardsService } from '../../../api/services/timecards.service'
import { TimesheetProjectService } from '../../../api/services/timesheet-project.service'
import { NavigationPaths } from '../../common/components/hierarch-menu/hierarchy-menu.component'
import { Filter, GroupBy } from '../../common/model/hierarchy-menu.model'
import { DialogService } from '../../common/services/dialog.service'
import { EmployeeFilterService } from '../../common/services/employee-filter.service'
import { LoaderService } from '../../common/services/loader.service'
import { SnackbarService } from '../../common/services/snackbar.service'
import { TimekeeperService } from '../../common/services/time-keeper.service'
import { notUndefined } from '../../common/utils/conditional-assert'
import { Utils } from '../../common/utils/utils'
import { UnsubscribeOnDestroyAdapter } from '../../shared/adapter/unsubscribe-on-destroy-adapter'
import { IDateable } from '../components/date-selector/idateable.interface'
import { TimeCardContainer } from './model/timecard-container.model'
import { TimecardDialogComponent } from './timecard-dialog/timecard-dialog.component'

@Component({
    selector: 'ppw-timecards',
    templateUrl: './timecards.component.html',
    styleUrls: ['./timecards.component.scss']
})
export class TimecardsComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
    filter!: Filter<TimeCardSearchCriteria>
    pageTitle = 'Timecards'
    displayedColumns: Array<string> = ['name', 'HTC', 'HNC', 'HTT', 'DTC', 'DNC', 'DTT']
    datasource$: Observable<Array<TimeCardContainer> | undefined>
    selectedTimeCards: Array<TimeCardContainer> = new Array<TimeCardContainer>()
    employeeSubjectList!: EmployeePaginatedList
    timeSheetProjectSubjectList!: TimesheetProjectPaginatedList

    dateSelectorConfig = {
        icon: 'search',
        iconTitle: 'Search',
        page: NavigationPaths.timecards
    }

    private readonly prefix = 'TimecardsComponent'
    private readonly datasourceSubject: BehaviorSubject<Array<TimeCardContainer> | undefined> = new BehaviorSubject<
        Array<TimeCardContainer> | undefined
    >(undefined)
    private latestDatasource!: Array<TimeCardContainer>
    private latestSelectedTimecardIndex?: number

    constructor(
        private readonly route: ActivatedRoute,
        private readonly loaderService: LoaderService,
        private readonly timeCardsService: TimeCardsService,
        private readonly snackbarService: SnackbarService,
        private readonly timekeeperService: TimekeeperService,
        private readonly employeeFilterService: EmployeeFilterService,
        private readonly dialogService: DialogService,
        private readonly employeeService: EmployeeService,
        private readonly timeSheetProjectService: TimesheetProjectService
    ) {
        super()
        this.datasource$ = this.datasourceSubject.asObservable()
    }

    get hasLockedInSelection(): boolean {
        return this.selectedTimeCards.some(
            (container: TimeCardContainer) => container.timecardStatusEnum === TimeCardStatus.LOCKED
        )
    }

    async ngOnInit(): Promise<void> {
        this.setFilteredData()
        this.startEmployeeFilterSubscription()
        await this.fetchTimecards()
    }

    setFilteredData(): void {
        const lastEmployeeFilter = this.employeeFilterService.lastSelectedEmployee
        this.filter = {
            searchParams: this.getTimeCardSearchCriteria(),
            groupBy: [],
            employeeId: lastEmployeeFilter ? lastEmployeeFilter.id : undefined
        }
        this.subscriptions.add(
            this.route.data.subscribe((data) => {
                this.filter.groupBy = data.groupBy
            })
        )
    }

    async fetchTimecardsWithDates(dateable: IDateable): Promise<void> {
        this.filter.searchParams.startDate = dateable.startDate
        this.filter.searchParams.endDate = dateable.endDate
        await this.fetchTimecards()
    }

    /**
     * Opens up a timecard and returns a collection of the newly added items.
     */
    select(timecard: TimeCardContainer, event?: MouseEvent): Array<TimeCardContainer> {
        let result = new Array<TimeCardContainer>()
        if (timecard.children === undefined) {
            if (event && (event.ctrlKey || event.metaKey)) {
                if (this.selectedTimeCards.includes(timecard)) {
                    this.removeTimecardFromSelection(timecard)
                } else {
                    this.selectedTimeCards.push(timecard)
                    this.selectedTimeCards = [...this.selectedTimeCards]
                    this.latestSelectedTimecardIndex = this.latestDatasource.findIndex((t) => t === timecard)
                }
            } else if (this.selectedTimeCards.length > 0 && event && event.shiftKey) {
                // Tried to simulate the shift functionality in windows as much as possible.
                let currentIndex = 0
                const startIndex = notUndefined(this.latestSelectedTimecardIndex)
                const endIndex = this.latestDatasource.findIndex((t) => t === timecard)
                this.selectedTimeCards = []
                this.latestDatasource.forEach((row) => {
                    if (
                        !row.children &&
                        ((currentIndex >= startIndex && currentIndex <= endIndex) ||
                            (currentIndex <= startIndex && currentIndex >= endIndex))
                    ) {
                        this.selectedTimeCards.push(row)
                    }
                    currentIndex++
                })
            } else {
                this.selectedTimeCards = [timecard]
                this.latestSelectedTimecardIndex = this.latestDatasource.findIndex((t) => t === timecard)
            }
        } else {
            timecard.isExpanded = !timecard.isExpanded
            if (!timecard.isExpanded) {
                timecard.children.forEach(() => {
                    const removedChildren = this.removeChildrenFromDataSource(timecard)
                    const timecardsToRemoveFromSelection = this.selectedTimeCards.filter((t) =>
                        removedChildren.includes(t)
                    )
                    timecardsToRemoveFromSelection.forEach((t) => {
                        this.removeTimecardFromSelection(t)
                    })
                })
            } else {
                const timecardIndex = this.latestDatasource.findIndex((datasource) => datasource === timecard)
                const childIndentLvl: number = timecard.indentLvl ? timecard.indentLvl + 1 : 1
                const childrenToAdd: Array<TimeCardContainer> = timecard.children.map(
                    (child) => ({ ...child, indentLvl: childIndentLvl, parent: timecard } as TimeCardContainer)
                )
                this.latestDatasource.splice(timecardIndex, 1, timecard, ...childrenToAdd)
                result = childrenToAdd
            }
        }
        this.datasourceSubject.next(this.latestDatasource)

        return result
    }

    dbClickTimecard(element: TimeCardContainer): void {
        if (!element.children) {
            if (element.timecardStatusEnum === TimeCardStatus.LOCKED) {
                this.detailTimecard()
            } else {
                this.editTimecard()
            }
        } else {
            if (element.indentLvl !== undefined) {
                const tempSelectedTimecards = this.selectedTimeCards
                const tempLatestSelectedTimecardIndex = this.latestSelectedTimecardIndex
                this.recursivelySelectAllTimeCards(element)
                this.selectedTimeCards = tempSelectedTimecards // Don't override the selectedTimecards
                this.latestSelectedTimecardIndex = tempLatestSelectedTimecardIndex // Don't override the latestSelectedTimecardIndex
            } else {
                this.snackbarService.promtSnackBar('Double-click is disabled for the root item (may take too long)')
            }
        }
    }

    getHours(seconds: number): string {
        return Utils.secondsAsHours(seconds)
    }

    getDays(seconds: number): string {
        return Utils.secondsAsWorkingDay(seconds)
    }

    createTimeCard(): void {
        this.showTimeCardDialog(undefined, CrudOps.Create, this.employeeSubjectList, this.timeSheetProjectSubjectList)
    }

    deleteTimecard(): void {
        this.showTimeCardDialog(
            this.selectedTimeCards,
            CrudOps.Delete,
            this.employeeSubjectList,
            this.timeSheetProjectSubjectList
        )
    }

    editTimecard(): void {
        this.showTimeCardDialog(
            this.selectedTimeCards,
            CrudOps.Update,
            this.employeeSubjectList,
            this.timeSheetProjectSubjectList
        )
    }

    copyTimecard(): void {
        this.showTimeCardDialog(
            this.selectedTimeCards,
            CrudOps.Create,
            this.employeeSubjectList,
            this.timeSheetProjectSubjectList
        )
    }

    detailTimecard(): void {
        this.showTimeCardDialog(
            this.selectedTimeCards,
            CrudOps.Read,
            this.employeeSubjectList,
            this.timeSheetProjectSubjectList
        )
    }

    private showTimeCardDialog(
        selectedTimeCardContainerGroup: Array<TimeCardContainer> | undefined,
        crud: CrudOps,
        employeeSubjectList: EmployeePaginatedList,
        timeSheetProjectSubjectList: TimesheetProjectPaginatedList
    ): void {
        const sub: Subscription = this.dialogService
            .open(TimecardDialogComponent, {
                selectedTimeCardContainerGroup,
                crud,
                employeeSubjectList,
                timeSheetProjectSubjectList
            })
            .afterClosed()
            .subscribe(async (response) => {
                if (response !== undefined) {
                    const newlyCreatedTimecards = []
                    if (this.filter.groupBy[0] === 'tag') {
                        await this.fetchTimecards()
                    } else {
                        if (response.crud === CrudOps.Create) {
                            if (response.timecards) {
                                response.timecards.forEach((timecard: TimeCardContainer) => {
                                    newlyCreatedTimecards.push(this.addTimecardToDatasource(timecard))
                                })
                            }
                        } else if (response.crud === CrudOps.Update) {
                            if (response.timecards) {
                                this.selectedTimeCards.forEach((timecard: TimeCardContainer) => {
                                    this.removeTimecardFromDataSource(timecard)
                                })
                                response.timecards.forEach((timecard: TimeCardContainer) => {
                                    newlyCreatedTimecards.push(this.addTimecardToDatasource(timecard))
                                })
                            } else {
                                this.removeTimecardFromDataSource(this.selectedTimeCards[0])
                                newlyCreatedTimecards.push(this.addTimecardToDatasource(response.timecard))
                            }
                        } else if (response.crud === CrudOps.Delete) {
                            if (this.selectedTimeCards.length > 1) {
                                this.selectedTimeCards.forEach((timecard: TimeCardContainer) => {
                                    this.removeTimecardFromDataSource(timecard)
                                })
                            } else {
                                this.removeTimecardFromDataSource(this.selectedTimeCards[0])
                            }
                        }
                    }
                    if (response.crud !== CrudOps.Delete || response.crud !== CrudOps.DeleteGroup) {
                        this.selectedTimeCards = []
                        newlyCreatedTimecards.forEach((t) => {
                            this.tryToOpenPreviouslySelectedTimecard(t)
                        })
                    }
                    this.promptMessageBasedOnCrud(response.crud)
                    this.datasourceSubject.next(this.latestDatasource)
                }
                sub.unsubscribe() // clean this up before the destruction of the component
            })
    }

    private promptMessageBasedOnCrud(crud: CrudOps): void {
        if (crud === CrudOps.Create) {
            this.snackbarService.promtSnackBar('Timecard created')
        } else if (crud === CrudOps.Update || crud === CrudOps.UpdateGroup) {
            this.snackbarService.promtSnackBar('Timecard(s) updated')
        } else {
            this.snackbarService.promtSnackBar('Timecard(s) deleted')
        }
    }

    private async fetchTimecards(): Promise<void> {
        this.loaderService.startLoading()
        try {
            const timecardPaginatedList = await this.timeCardsService.getTimeCards(this.filter.searchParams)
            this.employeeSubjectList = await this.employeeService.getAllEmployees()
            this.timeSheetProjectSubjectList = await this.timeSheetProjectService.getAllTimesheetProjects()
            const groupedTimecards = await this.timeCardsService.groupTimeCards(
                timecardPaginatedList.items as Array<TimeCardContainer>,
                this.filter.groupBy
            )
            this.latestDatasource = [...groupedTimecards]
            groupedTimecards.forEach((timecard) => {
                this.select(timecard, undefined)
            })
            this.datasourceSubject.next(this.latestDatasource)
        } catch (err) {
            this.snackbarService.handleError(this.prefix, err, true)
        } finally {
            this.loaderService.stopLoading()
        }
    }

    private startEmployeeFilterSubscription(): void {
        this.subscriptions.add(
            this.employeeFilterService.employee$
                .pipe(filter((e) => !(e === undefined && this.filter.searchParams.employeeId === undefined)))
                .subscribe((employee?: Employee) => {
                    this.filter.searchParams.employeeId = employee ? employee.id : undefined
                    void this.fetchTimecards()
                })
        )
    }

    private getTimeCardSearchCriteria(): TimeCardSearchCriteria {
        const dateableCriteria = this.timekeeperService.getTimeCardTimeFrame()
        if (dateableCriteria.month === null) {
            dateableCriteria.month = undefined
        }
        const timesearchCriteria = new TimeCardSearchCriteria()
        timesearchCriteria.pageSize = 200000
        timesearchCriteria.page = 1
        timesearchCriteria.excludedStatuses = [TimeCardStatus.DELETED]
        timesearchCriteria.startDate = dateableCriteria.startDate
        timesearchCriteria.endDate = dateableCriteria.endDate
        timesearchCriteria.employeeId = this.employeeFilterService.lastSelectedEmployee
            ? this.employeeFilterService.lastSelectedEmployee.id
            : undefined

        return timesearchCriteria
    }

    private removeTimecardFromSelection(timeCard: TimeCardContainer): void {
        const indexToDelete = this.selectedTimeCards.findIndex((t) => t === timeCard)
        this.selectedTimeCards.splice(indexToDelete, 1)
        this.selectedTimeCards = [...this.selectedTimeCards]
    }

    private removeChildrenFromDataSource(timecard: TimeCardContainer): Array<TimeCardContainer> {
        const childrenToRemove = this.latestDatasource.filter((datasource) => datasource.parent === timecard)
        this.latestDatasource = this.latestDatasource.filter((datasource) => !childrenToRemove.includes(datasource))
        if (timecard.children) {
            childrenToRemove.forEach((child) => {
                childrenToRemove.push(...this.removeChildrenFromDataSource(child))
            })
        }

        return childrenToRemove
    }

    private removeTimecardFromDataSource(timecard: TimeCardContainer): void {
        const parent = notUndefined(timecard.parent)
        this.recursivelyRemoveTimecardParentsIfEmpty(timecard)
        this.timeCardsService.updateTotalsForAllParents(this.findParentWithChildren(parent))
    }

    private findParentWithChildren = (timecard: TimeCardContainer): TimeCardContainer => {
        if (!timecard.children || timecard.children.length === 0) {
            return this.findParentWithChildren(notUndefined(timecard.parent))
        }

        return timecard
    }

    private addTimecardToDatasource(timecard: TimeCardContainer): TimeCardContainer {
        const groupByArray = ['year' as GroupBy, ...this.filter.groupBy]
        const group = this.timeCardsService.findGroupOfTimecard(timecard, this.latestDatasource, groupByArray)
        if (group) {
            const groupChildren = notUndefined(group.children)
            const allChildren = this.getAllChildren(groupChildren)
            if (groupByArray.length === 0) {
                groupChildren.push(timecard)
                timecard.parent = group
            } else if (groupByArray.length === 1) {
                group.children = this.timeCardsService.groupTimeCardContainer(
                    [...allChildren, timecard],
                    groupByArray[0]
                )
                const parent = group.children.find((t) =>
                    notUndefined(t.children).find((child) => child.id === timecard.id)
                )
                notUndefined(parent).parent = group
                timecard.parent = parent
            } else {
                const result = this.timeCardsService.groupTimeCardContainer([...allChildren, timecard], groupByArray[0])
                groupByArray.shift()
                this.timeCardsService.groupByAllChildren(result, groupByArray)
                group.children = result
                group.children.forEach((child) => (child.parent = group))
            }
            this.addNewTimecardAsChildToParent(group)
            this.timeCardsService.updateTotalsForAllParents(group)
            if (group.isExpanded) {
                this.removeChildrenFromDataSource(group)
                this.refreshTimecardsChildren(group)
            }
        } else {
            const result = this.timeCardsService.groupTimeCardContainer([timecard], groupByArray[0])
            groupByArray.shift()
            this.timeCardsService.groupByAllChildren(result, groupByArray)
            this.latestDatasource.push(result[0])
        }

        return timecard
    }

    private getAllChildren = (timecards: Array<TimeCardContainer>): Array<TimeCardContainer> => {
        const result: Array<TimeCardContainer> = []
        timecards.forEach((t) => {
            if (!t.children || t.children.length === 0) {
                result.push(t)
            } else {
                result.push(...this.getAllChildren(t.children))
            }
        })

        return result
    }

    private getAllParents = (timeCardContainer: TimeCardContainer): Array<TimeCardContainer> => {
        const result = []
        const parent = timeCardContainer.parent
        if (parent !== undefined) {
            result.push(parent)
            result.push(...this.getAllParents(parent))
        }

        return result
    }

    private refreshTimecardsChildren(timecard: TimeCardContainer): void {
        const timecardIndex = this.latestDatasource.findIndex((datasource) => datasource === timecard)
        const childIndentLvl: number = timecard.indentLvl ? timecard.indentLvl + 1 : 1
        const childrenToAdd: Array<TimeCardContainer> = notUndefined(timecard.children).map(
            (child) =>
                ({
                    ...child,
                    indentLvl: childIndentLvl,
                    parent: timecard
                } as unknown as TimeCardContainer)
        )
        this.latestDatasource.splice(timecardIndex, 1, timecard, ...childrenToAdd)
    }

    private recursivelyRemoveTimecardParentsIfEmpty(timecard: TimeCardContainer): void {
        const indexToRemove = this.latestDatasource.findIndex((d) => d === timecard)
        this.latestDatasource.splice(indexToRemove, 1)
        const parent = timecard.parent
        if (parent) {
            const childIndexToRemove = notUndefined(parent.children).findIndex((t) =>
                t.id ? t.id === timecard.id : t.name === timecard.name
            )
            notUndefined(parent.children).splice(childIndexToRemove, 1)
            if (notUndefined(notUndefined(timecard.parent).children).length === 0) {
                this.recursivelyRemoveTimecardParentsIfEmpty(notUndefined(timecard.parent))
            }
        }
    }

    private addNewTimecardAsChildToParent(newTimecard: TimeCardContainer): void {
        if (newTimecard.parent !== undefined) {
            const cloneTimecard: TimeCardContainer = {
                ...newTimecard,
                isExpanded: false
            } as unknown as TimeCardContainer
            const parent = notUndefined(
                this.latestDatasource.find((t) => t.name === notUndefined(newTimecard.parent).name)
            )
            const childIndexToReplace = notUndefined(parent.children).findIndex((t) => t.name === newTimecard.name)
            notUndefined(parent.children).splice(childIndexToReplace, 1, cloneTimecard)
        }
    }

    private tryToOpenPreviouslySelectedTimecard(timecardContainer: TimeCardContainer): void {
        const previousParents: Array<TimeCardContainer> = []
        const parents = this.getAllParents(timecardContainer)
        parents.reverse()
        parents.forEach((timeCardContainer) => {
            const elementToSelect = this.latestDatasource.find(
                (d) =>
                    d.name === timeCardContainer.name &&
                    (this.timeCardsService.hasParents(previousParents, d) || previousParents.length === 0)
            )
            if (elementToSelect && !elementToSelect.isExpanded) {
                this.select(elementToSelect)
            }
            previousParents.push(timeCardContainer)
        })
        const finalElementToSelect = this.latestDatasource.find((d) => d.id === timecardContainer.id)
        const event = { ctrlKey: true } as unknown as MouseEvent
        if (finalElementToSelect) {
            this.select(finalElementToSelect, event)
        }
    }

    private recursivelySelectAllTimeCards(timeCardContainer: TimeCardContainer): void {
        if (timeCardContainer.isExpanded) {
            this.select(timeCardContainer) // the row was already expanded, close it and open it again.
        }
        const openedTimecards = this.select(timeCardContainer)
        if (openedTimecards && openedTimecards.length > 0) {
            openedTimecards.forEach((t) => {
                this.recursivelySelectAllTimeCards(t)
            })
        }
    }
}
